/* eslint-disable id-blacklist */
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'

import {
  ActionButton,
  AvailableLocations,
  AvailableProductInput,
  AvailableSupplierInput,
  FilterButtonsStack,
  FilterFieldsStack,
  FilterStack,
} from 'src/common/components'
import { useFilter } from 'src/common/hooks'
import { IAllSearchFilterFields, IOverviewsFilter } from 'src/common/interfaces'
import { noOp } from 'src/common/lib'
import {
  constructFilterString,
  isFilterEmpty,
  pushNewFilterRule,
} from 'src/common/utils'

export default function OverviewsFilter({
  changeFilterString,
  resetSearch: resetFilter,
  ...rest
}: Readonly<IOverviewsFilter>): JSX.Element {
  const CACHE_KEY = 'product-regularity-filter'
  const defaultValues: IAllSearchFilterFields = {
    aanvoerderNummer: undefined,
    productCode: undefined,
    vestigingCode: undefined,
  }

  const setFilterString = (newValues: IAllSearchFilterFields) => {
    if (isFilterEmpty(newValues)) {
      changeFilterString(undefined)
      return
    }
    const newFilterString = constructFilterString(newValues, pushNewFilterRule)
    changeFilterString(newFilterString)
  }

  /*
  Hooks
  */
  const { t } = useTranslation()

  const {
    filter,
    clear,
    handleChange: updateEntity,
    handleRequest: search,
  } = useFilter<IAllSearchFilterFields>({
    cacheKey: CACHE_KEY,
    defaultFilter: defaultValues,
    resetFilter,
    setFilterString,
  })

  /*
  Effects
  */
  useEffect(() => {
    clear()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  /*
  Render
  */
  return (
    <FilterStack {...rest}>
      <FilterFieldsStack>
        <AvailableSupplierInput
          changeSupplierNumber={value =>
            value > 0
              ? updateEntity('aanvoerderNummer', value)
              : updateEntity('aanvoerderNummer', undefined)
          }
          value={filter.aanvoerderNummer ?? undefined}
          changeValid={noOp}
          sx={{ maxWidth: 180 }}
        />
        <AvailableProductInput
          onChange={value =>
            value > 0
              ? updateEntity('productCode', value)
              : updateEntity('productCode', undefined)
          }
          value={filter.productCode ?? undefined}
          values={filter}
          changeValid={noOp}
          sx={{ maxWidth: 180 }}
        />
        <AvailableLocations
          handleChange={updateEntity}
          inputValue={filter.vestigingCode}
          labelVisible
          sx={{ ml: 0.2, width: 160 }}
        />
      </FilterFieldsStack>
      <FilterButtonsStack>
        <ActionButton variant='outlined' onClick={clear}>
          {t('common.clear')}
        </ActionButton>
        <ActionButton type='submit' onClick={search}>
          {t('common.search')}
        </ActionButton>
      </FilterButtonsStack>
    </FilterStack>
  )
}
